import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { getWithRetry, resolveImg } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { usePlayer } from "@/context/PlayerContext";
import { warmOfflineLibrary, isCachedOffline } from "@/lib/offlineCache";
import { tr } from "@/lib/translate";
import { toast } from "sonner";
import { Play, Pause, Library as LibIcon, Search, Plus, X, MoreVertical, WifiOff, ListMusic } from "lucide-react";
import CopyrightNotice from "@/components/CopyrightNotice";
import ReleaseCard from "@/components/ReleaseCard";

export default function LibraryPage() {
  const { t } = useI18n();
  const [tracks, setTracks] = useState([]);
  const [projects, setProjects] = useState([]); // albums / EPs I own songs of
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("");
  const [releaseType, setReleaseType] = useState("");
  const { current, playing, playTrack, togglePlay } = usePlayer();

  const [playlists, setPlaylists] = useState([]);
  const [activePlaylistId, setActivePlaylistId] = useState(null); // null = "all tracks" view
  const [showNewPlaylist, setShowNewPlaylist] = useState(false);
  const [pickerTrack, setPickerTrack] = useState(null); // track being added to a playlist
  const [offlineIds, setOfflineIds] = useState(() => new Set());
  // Distinct from "library is empty" — a failed request used to silently
  // leave the list empty with zero indication anything went wrong,
  // indistinguishable from a genuinely empty library (same root cause as
  // the Feed/Store pages' identical bug).
  const [loadError, setLoadError] = useState(false);

  const loadPlaylists = async () => {
    try {
      const { data } = await api.get("/playlists");
      setPlaylists(data);
    } catch { /* non-critical */ }
  };

  useEffect(() => { loadPlaylists(); }, []);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (genre) params.genre = genre;
      if (releaseType) params.release_type = releaseType;
      const { data } = await getWithRetry("/library", { params });
      setTracks(data);
      warmOfflineLibrary(data); // fire-and-forget, only does anything while online
    } catch {
      setTracks([]);
      setLoadError(true);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, genre, releaseType]);

  // Track which owned tracks are already cached for offline playback, so we
  // can show a small indicator instead of pretending it's uncertain.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(tracks.map(async (tr) => [tr.id, await isCachedOffline(tr.id)]));
      if (cancelled) return;
      setOfflineIds(new Set(entries.filter(([, cached]) => cached).map(([id]) => id)));
    })();
    return () => { cancelled = true; };
  }, [tracks]);

  useEffect(() => {
    api.get("/library/releases").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const genres = useMemo(() => Array.from(new Set(tracks.map((tr) => tr.genre).filter(Boolean))), [tracks]);
  const activePlaylist = playlists.find((p) => p.id === activePlaylistId) || null;
  const shownTracks = activePlaylist ? activePlaylist.tracks : tracks;

  const createPlaylist = async (name) => {
    try {
      const { data } = await api.post("/playlists", { name });
      setPlaylists((prev) => [data, ...prev]);
      setActivePlaylistId(data.id);
      setShowNewPlaylist(false);
      toast.success(tr("Playlist created"));
    } catch { toast.error(tr("Could not save")); }
  };

  const deletePlaylist = async (id) => {
    try {
      await api.delete(`/playlists/${id}`);
      setPlaylists((prev) => prev.filter((p) => p.id !== id));
      if (activePlaylistId === id) setActivePlaylistId(null);
    } catch { toast.error(tr("Could not save")); }
  };

  const addToPlaylist = async (playlistId, trackId) => {
    try {
      const { data } = await api.post(`/playlists/${playlistId}/tracks`, { track_id: trackId });
      setPlaylists((prev) => prev.map((p) => (p.id === playlistId ? data : p)));
      toast.success(tr("Added to playlist"));
    } catch { toast.error(tr("Could not save")); }
    finally { setPickerTrack(null); }
  };

  const removeFromPlaylist = async (playlistId, trackId) => {
    try {
      const { data } = await api.delete(`/playlists/${playlistId}/tracks/${trackId}`);
      setPlaylists((prev) => prev.map((p) => (p.id === playlistId ? data : p)));
    } catch { toast.error(tr("Could not save")); }
  };

  if (loading) return <div className="text-center text-zinc-500 py-12">{t.common.loading}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6" data-testid="library-page">
      <header className="anim-in mb-6 flex items-end gap-4">
        <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] grid place-items-center shadow-[0_0_30px_rgba(255,0,80,0.4)]">
          <LibIcon className="w-10 h-10 text-white" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF0050] font-bold">PLAYLIST</p>
          <h1 className="text-3xl sm:text-5xl font-black font-display text-white">{t.library.title}</h1>
          <p className="text-zinc-400 mt-1 text-sm">{tracks.length} tracks</p>
        </div>
      </header>

      {projects.length > 0 && !activePlaylistId && (
        <section className="mb-8 anim-in" data-testid="library-projects">
          <h2 className="text-sm font-black uppercase tracking-wider text-zinc-300 mb-3">Mes albums, EP et maxis</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {projects.map((p) => (
              <ReleaseCard key={p.id} release={p} badge={p.complete ? "Complet" : `${p.owned_paid_count ?? p.owned_count}/${p.paid_count ?? p.tracks_count}`} />
            ))}
          </div>
        </section>
      )}

      {/* Playlist chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6 anim-in" data-testid="playlist-chips">
        <button
          onClick={() => setActivePlaylistId(null)}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition ${!activePlaylistId ? "bg-white text-black" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}
          data-testid="playlist-chip-all"
        >
          <LibIcon className="w-3.5 h-3.5" /> {t.library.allTracks}
        </button>
        {playlists.map((p) => (
          <button
            key={p.id}
            onClick={() => setActivePlaylistId(p.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition ${activePlaylistId === p.id ? "bg-white text-black" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}
            data-testid={`playlist-chip-${p.id}`}
          >
            <ListMusic className="w-3.5 h-3.5" /> {p.name} <span className="opacity-60">({p.track_count})</span>
          </button>
        ))}
        <button
          onClick={() => setShowNewPlaylist(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-white/5 text-zinc-400 hover:bg-white/10 border border-dashed border-white/15 transition"
          data-testid="playlist-create-btn"
        >
          <Plus className="w-3.5 h-3.5" /> {t.library.newPlaylist}
        </button>
      </div>

      {!activePlaylist && (
        <div className="flex flex-wrap gap-2 mb-6 anim-in">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.library?.searchPh ?? "Titre ou artiste…"} className="input-base pl-9 text-sm" data-testid="library-search" />
          </div>
          <select value={releaseType} onChange={(e) => setReleaseType(e.target.value)} className="input-base text-sm" data-testid="library-type" style={{ maxWidth: 140 }}>
            <option value="">Tous types</option>
            <option value="single">Single</option>
            <option value="ep">EP</option>
            <option value="album">Album</option>
            <option value="maxi">Maxi</option>
          </select>
          <select value={genre} onChange={(e) => setGenre(e.target.value)} className="input-base text-sm" data-testid="library-genre" style={{ maxWidth: 160 }}>
            <option value="">Tous genres</option>
            {genres.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      )}

      {activePlaylist && (
        <div className="flex items-center justify-between mb-4 anim-in">
          <h2 className="text-xl font-black font-display text-white">{activePlaylist.name}</h2>
          <button
            onClick={() => deletePlaylist(activePlaylist.id)}
            className="text-xs font-bold text-red-400 hover:text-red-300 px-3 py-1.5 rounded-full bg-red-500/10 hover:bg-red-500/20 transition"
            data-testid="playlist-delete-btn"
          >
            {t.library.deletePlaylist}
          </button>
        </div>
      )}

      {loadError ? (
        <div className="card-flat p-12 text-center" data-testid="library-load-error">
          <p className="text-zinc-400 mb-4">{t.library.loadError || "La bibliothèque n'a pas pu charger — le serveur met parfois quelques secondes à répondre."}</p>
          <button onClick={load} className="btn-primary" data-testid="library-retry-btn">{t.library.retry || "Réessayer"}</button>
        </div>
      ) : shownTracks.length === 0 ? (
        <div className="card-flat p-12 text-center">
          <p className="text-zinc-400 mb-4">{activePlaylist ? t.library.emptyPlaylist : t.library.empty}</p>
          {!activePlaylist && <Link to="/store" className="btn-primary inline-block">{t.library.goStore}</Link>}
        </div>
      ) : (
        <div className="card-flat overflow-hidden" data-testid="library-list">
          {shownTracks.map((trk, i) => {
            const isCurrent = current?.id === trk.id;
            const isPlaying = isCurrent && playing;
            const isOffline = offlineIds.has(trk.id);
            return (
              <div
                key={trk.id}
                onClick={() => isCurrent ? togglePlay() : playTrack(trk, shownTracks)}
                className="flex items-center gap-3 sm:gap-4 p-3 hover:bg-white/5 transition border-b border-white/5 last:border-0 cursor-pointer"
                data-testid={`library-row-${trk.id}`}
              >
                <span className="w-6 text-center text-xs text-zinc-500 tabular-nums">{i + 1}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); isCurrent ? togglePlay() : playTrack(trk, shownTracks); }}
                  className="relative w-12 h-12 rounded-lg overflow-hidden shrink-0 group"
                  data-testid={`library-play-${trk.id}`}
                >
                  <img src={resolveImg(trk.cover)} alt="" className="w-full h-full object-cover" />
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 grid place-items-center transition">
                    {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{trk.title}</p>
                  <p className="text-xs text-zinc-400 truncate">{trk.artist_name}</p>
                </div>
                {isOffline && (
                  <span title={t.library.offlineReady} className="hidden sm:inline-flex items-center gap-1 chip bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <WifiOff className="w-3 h-3" /> {t.library.offlineBadge}
                  </span>
                )}
                <span className="hidden sm:inline chip bg-white/5 text-zinc-300 border border-white/5">{trk.genre}</span>
                {activePlaylist ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromPlaylist(activePlaylist.id, trk.id); }}
                    className="p-2 rounded-full hover:bg-white/10 text-zinc-500 hover:text-red-400 transition"
                    data-testid={`playlist-remove-${trk.id}`}
                    aria-label={t.library.removeFromPlaylist}
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setPickerTrack(trk); }}
                    className="p-2 rounded-full hover:bg-white/10 text-zinc-500 hover:text-white transition"
                    data-testid={`library-add-playlist-${trk.id}`}
                    aria-label={t.library.addToPlaylist}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-white/5">
        <CopyrightNotice />
      </div>

      {showNewPlaylist && (
        <NewPlaylistModal onClose={() => setShowNewPlaylist(false)} onCreate={createPlaylist} t={t} />
      )}
      {pickerTrack && (
        <AddToPlaylistModal
          track={pickerTrack}
          playlists={playlists}
          onClose={() => setPickerTrack(null)}
          onPick={(playlistId) => addToPlaylist(playlistId, pickerTrack.id)}
          onCreateNew={() => { setShowNewPlaylist(true); setPickerTrack(null); }}
          t={t}
        />
      )}
    </div>
  );
}

function NewPlaylistModal({ onClose, onCreate, t }) {
  const [name, setName] = useState("");
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="new-playlist-modal">
      <div className="w-full max-w-sm bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close"><X className="w-4 h-4 text-zinc-400" /></button>
        <h3 className="text-xl font-black font-display text-white mb-4">{t.library.newPlaylistTitle}</h3>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onCreate(name.trim()); }} className="space-y-3">
          <input
            autoFocus
            className="input-base"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.library.playlistNamePh}
            data-testid="new-playlist-name"
          />
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">{t.library.cancel}</button>
            <button type="submit" disabled={!name.trim()} className="btn-primary flex-1 disabled:opacity-60" data-testid="new-playlist-submit">{t.library.create}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddToPlaylistModal({ track, playlists, onClose, onPick, onCreateNew, t }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="add-to-playlist-modal">
      <div className="w-full max-w-sm bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close"><X className="w-4 h-4 text-zinc-400" /></button>
        <h3 className="text-lg font-black font-display text-white mb-1">{t.library.addToPlaylist}</h3>
        <p className="text-xs text-zinc-500 mb-4 truncate">{track.title} — {track.artist_name}</p>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {playlists.length === 0 && (
            <p className="text-sm text-zinc-500 py-2">{t.library.emptyPlaylist}</p>
          )}
          {playlists.map((p) => (
            <button
              key={p.id}
              onClick={() => onPick(p.id)}
              className="w-full text-left px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm font-semibold flex items-center justify-between transition"
              data-testid={`add-to-playlist-option-${p.id}`}
            >
              <span className="truncate">{p.name}</span>
              <span className="text-xs text-zinc-500">{p.track_count}</span>
            </button>
          ))}
        </div>
        <button
          onClick={onCreateNew}
          className="w-full mt-3 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-white/15 text-zinc-400 hover:text-white hover:bg-white/5 text-sm font-bold transition"
          data-testid="add-to-playlist-new"
        >
          <Plus className="w-4 h-4" /> {t.library.newPlaylist}
        </button>
      </div>
    </div>
  );
}
